import {
  CATCHUP_FLOOR_PER_SEC,
  CATCHUP_RATE_PER_SEC,
  CINEMA_PHASES,
  ETA_ALMOST_THERE,
  ETA_FEW_SECONDS,
  ETA_LONGER_THAN_USUAL,
  ETA_MUCH_LONGER_THAN_USUAL,
  ETA_OVERDUE_GRACE_SEC,
  ETA_OVERDUE_LONG_SEC,
  PACE_HEAD_LEAD,
  PACE_LAST_CAP,
  PACE_LAST_TARGET_SHARE,
  PACE_MIN_CHASE_SEC,
  PACE_MIN_PER_SEC,
  PACE_MIN_SEGMENT,
  PACE_SOFT_ZONE,
} from './constants';

export interface ICinemaSegment {
  lo: number;
  hi: number;
}

export interface ICinemaPace {
  value: number;
  phase: number;
  starts: number[];
}

export interface ICinemaExpectation {
  sec: number;
  fromMs: number;
}

export interface ICinemaPaceStep {
  phaseIdx: number;
  expectation: ICinemaExpectation;
  nowMs: number;
  deltaSec: number;
}

const LAST = CINEMA_PHASES.length - 1;

const NOMINAL_WIDTHS = CINEMA_PHASES.map(
  (phase, i) => (CINEMA_PHASES[i + 1]?.threshold ?? 100) - phase.threshold,
);

export const createPace = (): ICinemaPace => ({
  value: 0,
  phase: 0,
  starts: [0],
});

const widthsFrom = (first: number, room: number): number[] => {
  const nominal = NOMINAL_WIDTHS.slice(first);
  const floor = PACE_MIN_SEGMENT * nominal.length;
  const spare = nominal.reduce(
    (total, width) => total + width - PACE_MIN_SEGMENT,
    0,
  );
  const share = spare > 0 ? Math.max(0, room - floor) / spare : 0;

  return nominal.map(
    (width) => PACE_MIN_SEGMENT + (width - PACE_MIN_SEGMENT) * share,
  );
};

const nominalEnd = (pace: ICinemaPace): number => {
  const start = pace.starts[pace.phase];

  return pace.phase === LAST
    ? 100
    : start + widthsFrom(pace.phase, 100 - start)[0];
};

const currentEnd = (pace: ICinemaPace): number =>
  pace.phase === LAST
    ? 100
    : Math.max(nominalEnd(pace), pace.value + PACE_HEAD_LEAD);

const capOf = (phase: number): number =>
  phase === LAST
    ? PACE_LAST_CAP
    : 100 - (LAST - phase) * PACE_MIN_SEGMENT - PACE_HEAD_LEAD;

const targetOf = (pace: ICinemaPace): number => {
  const start = pace.starts[pace.phase];

  return pace.phase === LAST
    ? start + (100 - start) * PACE_LAST_TARGET_SHARE
    : nominalEnd(pace);
};

export const segmentsOf = (pace: ICinemaPace): ICinemaSegment[] => {
  const passed = pace.starts
    .slice(0, pace.phase)
    .map((lo, i) => ({ lo, hi: pace.starts[i + 1] }));
  const current = { lo: pace.starts[pace.phase], hi: currentEnd(pace) };

  if (pace.phase === LAST) {
    return [...passed, current];
  }

  let lo = current.hi;
  const ahead = widthsFrom(pace.phase + 1, 100 - current.hi).map((width) => {
    const segment = { lo, hi: lo + width };
    lo += width;

    return segment;
  });
  ahead[ahead.length - 1].hi = 100;

  return [...passed, current, ...ahead];
};

export const remainingSecOf = (
  expectation: ICinemaExpectation,
  nowMs: number,
): number => Math.max(0, expectation.sec - (nowMs - expectation.fromMs) / 1000);

const enterPhase = (pace: ICinemaPace, phaseIdx: number): ICinemaPace => {
  let next = pace;

  while (next.phase < Math.min(phaseIdx, LAST)) {
    next = {
      ...next,
      phase: next.phase + 1,
      starts: [...next.starts, currentEnd(next)],
    };
  }

  return next;
};

export const advancePace = (
  pace: ICinemaPace,
  step: ICinemaPaceStep,
): ICinemaPace => {
  const entered = enterPhase(pace, step.phaseIdx);
  const start = entered.starts[entered.phase];

  if (entered.value < start) {
    const catchUp = Math.max(
      (start - entered.value) *
        (1 - Math.exp(-CATCHUP_RATE_PER_SEC * step.deltaSec)),
      CATCHUP_FLOOR_PER_SEC * step.deltaSec,
    );

    return { ...entered, value: Math.min(start, entered.value + catchUp) };
  }

  const minRate = PACE_MIN_PER_SEC[entered.phase];
  const target = targetOf(entered);
  const chase =
    step.phaseIdx >= entered.phase && entered.value < target
      ? (target - entered.value) /
        Math.max(
          remainingSecOf(step.expectation, step.nowMs),
          PACE_MIN_CHASE_SEC,
        )
      : 0;
  const cap = capOf(entered.phase);
  const headroom = cap - entered.value;
  const rate =
    headroom < PACE_SOFT_ZONE
      ? Math.min(
          Math.max(chase, minRate),
          (minRate * Math.max(0, headroom)) / PACE_SOFT_ZONE,
        )
      : Math.max(chase, minRate);

  return {
    ...entered,
    value: Math.min(cap, entered.value + rate * step.deltaSec),
  };
};

const tailSecAfter = (phaseIdx: number): number =>
  CINEMA_PHASES.slice(phaseIdx + 1).reduce(
    (total, phase) => total + phase.expectedSec,
    0,
  );

export const remainingTotalSec = (
  phaseIdx: number,
  expectation: ICinemaExpectation,
  nowMs: number,
): number => remainingSecOf(expectation, nowMs) + tailSecAfter(phaseIdx);

export const overdueSecOf = (
  expectation: ICinemaExpectation,
  nowMs: number,
): number => (nowMs - expectation.fromMs) / 1000 - expectation.sec;

export const describeRemaining = (remainingSec: number): string => {
  if (remainingSec >= 105) {
    return `About ${Math.round(remainingSec / 60)} minutes to go.`;
  }

  if (remainingSec >= 75) {
    return 'About a minute and a half to go.';
  }

  if (remainingSec >= 45) {
    return 'About a minute to go.';
  }

  if (remainingSec >= 15) {
    return `About ${Math.round(remainingSec / 10) * 10} seconds to go.`;
  }

  if (remainingSec >= 8) {
    return 'About 10 seconds to go.';
  }

  return ETA_FEW_SECONDS;
};

export const describeOverdue = (
  phaseIdx: number,
  overdueSec: number,
): string => {
  if (phaseIdx === LAST) {
    return ETA_ALMOST_THERE;
  }

  return overdueSec > ETA_OVERDUE_LONG_SEC
    ? ETA_MUCH_LONGER_THAN_USUAL
    : ETA_LONGER_THAN_USUAL;
};

export const isOverdue = (
  expectation: ICinemaExpectation,
  nowMs: number,
): boolean => overdueSecOf(expectation, nowMs) > ETA_OVERDUE_GRACE_SEC;
